import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StatusFormData } from "../components/add-edit-stage/components/AddStatusAndSubStatus";
import { transformStatusesForBackend } from "../utils/transformStatusesForBackend";

/* ================= TYPES ================= */

export interface RollbackObj {
  parentId?: number;
  parentName?: string;
  subId?: number;
  subName?: string;
}

export interface EditStages {
  name: string;
  description: string;
  isTracks: boolean;
  isChoices: boolean;
  specificStatuses: boolean;
  collaborator: boolean;
  isAnchorPoint: boolean;
  anchorName: string;
  verifyNewThisPeriod: boolean;
  verifyExcludeRejected: boolean;
  verifyExcludeRollback: boolean;
  verifyPreferActive: boolean;
  verifyTieBreakEarliest: boolean;
  verifyPreferLatestStage: boolean;
  verifyCreditOnce: boolean;
  verifyPeriodGrain: "month" | "week";
  maxChoices: number;
  isAllowCustomName: boolean;
  isAutoPauseOther: boolean;
  isLeadOwnerAccess: boolean;
  isTakeOver: boolean;
  isAllowCollabRemoval: boolean;

  initialStatusCardsData: StatusFormData;
  defaultRollbackStatusObj: RollbackObj;

  qualifyingStatus: string;
  defaultQualifyingSubStatus: any;
  rejectionStatuses: string[];
  /** Sub-status-level rejections as `"statusName::subName"` keys. */
  rejectionSubStatuses: string[];

  isShowInLead: boolean;
  isShowInRoute: boolean;
  /** Stage-level "Follow-up needed" — OFF: leads/deals entering the stage
   *  get no default follow-up and are hidden from Overdue/Today/Later. */
  showInFollowups: boolean;
  /** Default follow-up offset in days on entering the stage (0 = same day).
   *  Status / sub-status values override it; null = 0. */
  followUpDays: number | null;
  /** Furthest a person may date a follow-up while in this stage, in days
   *  from today. Status / sub-status values override it; null = no cap. */
  maxFollowUpDays: number | null;
  /** "Prioritise latest deal" — when a lead's furthest deal sits in this
   *  stage, follow-ups on its earlier deals are muted for every role. Any
   *  level (stage / status / sub-status) ON is enough. */
  followUpLatestDealOnly: boolean;

  icon: string;
  updatedItem: object;
}

/* ================= INITIAL STATE ================= */

const initialState: EditStages = {
  name: "",
  description: "",
  isTracks: false,
  isChoices: false,
  specificStatuses: false,
  collaborator: false,
  isAnchorPoint: false,
  anchorName: "Verified",
  verifyNewThisPeriod: true,
  verifyExcludeRejected: true,
  verifyExcludeRollback: false,
  verifyPreferActive: true,
  verifyTieBreakEarliest: true,
  verifyPreferLatestStage: false,
  verifyCreditOnce: false,
  verifyPeriodGrain: "month",

  maxChoices: 8,
  isAllowCustomName: true,
  isAutoPauseOther: false,

  isLeadOwnerAccess: true,
  isTakeOver: true,
  isAllowCollabRemoval: true,

  qualifyingStatus: "",
  defaultQualifyingSubStatus: {},
  rejectionStatuses: [],
  rejectionSubStatuses: [],

  isShowInLead: false,
  isShowInRoute: false,
  showInFollowups: true,
  followUpDays: null,
  maxFollowUpDays: null,
  followUpLatestDealOnly: false,

  icon: "Home",
  updatedItem: {},

  defaultRollbackStatusObj: {},
  initialStatusCardsData: [
    {
      id: 1,
      statusName: "",
      subStatuses: [{ id: 0, name: "" }],
    },
  ],
};

/* ================= SLICE ================= */

const editStagesSlice = createSlice({
  name: "editStages",
  initialState,
  reducers: {
    /* -------- INIT EDIT VALUES -------- */
    setDefaultEditStageValues(state, action: PayloadAction<any>) {
      const payload = action.payload;

      state.name = payload.name;
      state.description = payload?.description || "";
      state.isTracks = payload.isTracks;
      state.isChoices = payload.isChild;
      state.specificStatuses = payload.isSpecific;
      state.collaborator = payload.isCollaborator;
      state.isAnchorPoint = payload.isAnchorPoint || false;
      state.anchorName = payload.anchorName ?? "Verified";
      state.verifyNewThisPeriod = payload.verifyNewThisPeriod ?? true;
      state.verifyExcludeRejected = payload.verifyExcludeRejected ?? true;
      state.verifyExcludeRollback = payload.verifyExcludeRollback ?? false;
      state.verifyPreferActive = payload.verifyPreferActive ?? true;
      state.verifyTieBreakEarliest = payload.verifyTieBreakEarliest ?? true;
      state.verifyPreferLatestStage = payload.verifyPreferLatestStage ?? false;
      state.verifyCreditOnce = payload.verifyCreditOnce ?? false;
      state.verifyPeriodGrain =
        payload.verifyPeriodGrain === "week" ? "week" : "month";
      state.isAllowCustomName = payload.isAllowCustomName || false;
      state.isAutoPauseOther = payload.isAutoPauseOtherChoices || false;
      // "Max Choices" (Stage.maxChilds). This was NEVER hydrated: the editor
      // opened on the initial 8 whatever the stage actually had, so reopening
      // it and pressing Save silently rewrote a configured cap back to 8.
      // `0` is a real stored value (non-child stages carry it) and must not be
      // coerced away by `||`.
      state.maxChoices =
        payload.maxChilds == null ? state.maxChoices : Number(payload.maxChilds);
      state.isLeadOwnerAccess = payload.isLeadOwner;
      state.isTakeOver = payload.isLeadTakeOver;
      state.isAllowCollabRemoval = payload.isAllowCollaboratorRemoval;
      state.isShowInLead = payload.isShowInLeadProfile;
      state.isShowInRoute = payload.isShowInSideRoute;
      state.showInFollowups = payload.showInFollowups !== false;
      state.followUpDays =
        payload.followUpDays == null ? null : Number(payload.followUpDays);
      state.maxFollowUpDays =
        payload.maxFollowUpDays == null ? null : Number(payload.maxFollowUpDays);
      state.followUpLatestDealOnly = payload.followUpLatestDealOnly === true;
      state.icon = payload.iconName;

      /* ✅ STATUS CARD INIT */
      state.initialStatusCardsData = payload.statuses.map((status: any) => ({
        id: status.id,
        statusName: status.name,
        // Follow-up visibility rides the card rows so the toggle next to
        // the tone bell persists through the normal Save changes flow.
        showInFollowups: status.showInFollowups !== false,
        followUpDays:
          status.followUpDays == null ? null : Number(status.followUpDays),
        maxFollowUpDays:
          status.maxFollowUpDays == null
            ? null
            : Number(status.maxFollowUpDays),
        followUpLatestDealOnly: status.followUpLatestDealOnly === true,
        // "Sequence wise" toggle on the status header.
        isSequential: status.isSequential === true,
        subStatuses:
          status.subStatuses?.length > 0
            ? status.subStatuses.map((sub: any) => ({
                id: sub.id,
                name: sub.name,
                showInFollowups: sub.showInFollowups !== false,
                followUpDays:
                  sub.followUpDays == null ? null : Number(sub.followUpDays),
                maxFollowUpDays:
                  sub.maxFollowUpDays == null
                    ? null
                    : Number(sub.maxFollowUpDays),
                followUpLatestDealOnly: sub.followUpLatestDealOnly === true,
              }))
            : [{ id: 0, name: "" }],
        preferences: Array.isArray(status.statusPreferences)
          ? status.statusPreferences.map((p: any) => ({
              id: p.id,
              customInputId: p.customInputId,
              customInputItemId: p.customInputItemId,
            }))
          : [],
        // Toggle defaults to ON when the status came back with at least one
        // persisted rule. Empty list → OFF, so the section stays collapsed
        // until the user explicitly opts in.
        preferencesEnabled:
          Array.isArray(status.statusPreferences) &&
          status.statusPreferences.length > 0,
      }));

      /* -------- FLAGS -------- */
      const qualifying = payload?.statuses.find((s: any) => s.isQualifying);
      const qualifyingSub = qualifying?.subStatuses?.find(
        (sub: any) => sub.isDefaultQualifying
      );
      const rollback = payload.statuses.find((s: any) => s.isRollback);

      const rollbackSub = payload.statuses
        .flatMap((s: any) => s.subStatuses || [])
        .find((sub: any) => sub.isDefaultRollback);

      state.qualifyingStatus = qualifying?.name || "";
      state.rejectionStatuses = payload.statuses
        .filter((s: any) => s.isRejection)
        .map((s: any) => s.name);
      state.rejectionSubStatuses = payload.statuses.flatMap((s: any) =>
        (s.subStatuses || [])
          .filter((sub: any) => sub.isRejection)
          .map((sub: any) => `${s.name}::${sub.name}`)
      );

      state.defaultRollbackStatusObj = {
        parentId: rollback?.id || 0,
        parentName: rollback?.name || "",
        subId: rollbackSub?.id || 0,
        subName: rollbackSub?.name || "",
      };

      state.defaultQualifyingSubStatus = {
        parentId: qualifying?.id || 0,
        parentName: qualifying?.name || "",
        subId: qualifyingSub?.id || 0,
        subName: qualifyingSub?.name || "",
      };
    },

    /* -------- ALL YOUR EXISTING REDUCERS (UNCHANGED) -------- */

    setCurrentRollBackStatusObj(state, action: PayloadAction<RollbackObj>) {
      state.defaultRollbackStatusObj = action.payload;
    },

    setEditinitialStatusCardsData: (state, action) => {
      // Check if the payload is a function
      if (typeof action.payload === "function") {
        // If it's a function, call it with the current state to get the new state
        state.initialStatusCardsData = action.payload(
          state.initialStatusCardsData
        );
      } else {
        // If it's the final value, set it directly

        state.initialStatusCardsData = action.payload;
      }
    },

    setEditStageIcon(state, action: PayloadAction<string>) {
      state.icon = action.payload;
    },

    setEditStagesName(state, action: PayloadAction<string>) {
      state.name = action.payload;
    },

    setEditStagesDescription(state, action: PayloadAction<string>) {
      state.description = action.payload;
    },

    setEditStagesIsTracks(state, action: PayloadAction<boolean>) {
      state.isTracks = action.payload;
    },

    setEditStagesIsChoices(state, action: PayloadAction<boolean>) {
      state.isChoices = action.payload;
    },

    setEditStagesShowInFollowups(state, action: PayloadAction<boolean>) {
      state.showInFollowups = action.payload;
    },

    setEditStagesFollowUpDays(state, action: PayloadAction<number | null>) {
      state.followUpDays = action.payload;
    },

    setEditStagesMaxFollowUpDays(state, action: PayloadAction<number | null>) {
      state.maxFollowUpDays = action.payload;
    },

    setEditStagesFollowUpLatestDealOnly(state, action: PayloadAction<boolean>) {
      state.followUpLatestDealOnly = action.payload;
    },

    setEditStagesSpecificStatuses(state, action: PayloadAction<boolean>) {
      state.specificStatuses = action.payload;
    },

    setEditStagesCollaborator(state, action: PayloadAction<boolean>) {
      state.collaborator = action.payload;
    },

    setEditStagesAnchorPoint(state, action: PayloadAction<boolean>) {
      state.isAnchorPoint = action.payload;
    },

    setEditStagesAnchorName(state, action: PayloadAction<string>) {
      state.anchorName = action.payload;
    },

    setEditStagesVerifyCondition(
      state,
      action: PayloadAction<{
        key:
          | "verifyNewThisPeriod"
          | "verifyExcludeRejected"
          | "verifyExcludeRollback"
          | "verifyPreferActive"
          | "verifyTieBreakEarliest"
          | "verifyPreferLatestStage"
          | "verifyCreditOnce";
        value: boolean;
      }>
    ) {
      state[action.payload.key] = action.payload.value;
    },

    setEditStagesPeriodGrain(state, action: PayloadAction<"month" | "week">) {
      state.verifyPeriodGrain = action.payload;
    },

    setEditStagesIsShowInLead(state, action: PayloadAction<boolean>) {
      state.isShowInLead = action.payload;
    },

    setEditStagesIsShowInRightSide(state, action: PayloadAction<boolean>) {
      state.isShowInRoute = action.payload;
    },

    setEditStagesIsLeadOwner(state, action: PayloadAction<boolean>) {
      state.isLeadOwnerAccess = action.payload;
    },

    setEditStagesIsTakeOver(state, action: PayloadAction<boolean>) {
      state.isTakeOver = action.payload;
    },

    setEditIsAllowCollabRemoval(state, action: PayloadAction<boolean>) {
      state.isAllowCollabRemoval = action.payload;
    },

    setEditMaxChoices(state, action: PayloadAction<number>) {
      state.maxChoices = action.payload;
    },

    setEditIsAllowCustomName(state, action: PayloadAction<boolean>) {
      state.isAllowCustomName = action.payload;
    },

    setEditIsAutoPauseOther(state, action: PayloadAction<boolean>) {
      state.isAutoPauseOther = action.payload;
    },

    setEditQualifyingStatus(state, action: PayloadAction<string>) {
      state.qualifyingStatus = action.payload;
    },

    setEditDefaultQualifyingSubStatus(state, action: PayloadAction<any>) {
      state.defaultQualifyingSubStatus = action.payload;
    },

    setEditRejectionStatuses(state, action: PayloadAction<string[]>) {
      state.rejectionStatuses = action.payload;
    },

    setEditRejectionSubStatuses(state, action: PayloadAction<string[]>) {
      state.rejectionSubStatuses = action.payload;
    },

    resetEditStages() {
      return initialState;
    },
  },
});

/* ================= SELECTOR ================= */

export const selectEditStageBackendVariables = (state: any) => {
  const s = state?.editStage;

  if (!s) {
    return {
      name: "",
      description: "",
      isTracks: false,
      isChild: false,
      maxChilds: 0,
      isSpecific: false,
      isCollaborator: false,
      isAnchorPoint: false,
      anchorName: null,
      verifyNewThisPeriod: true,
      verifyExcludeRejected: true,
      verifyExcludeRollback: false,
      verifyPreferActive: true,
      verifyTieBreakEarliest: true,
      verifyPreferLatestStage: false,
      verifyCreditOnce: false,
      verifyPeriodGrain: "month",
      isAllowCustomName: false,
      isAutoPauseOtherChoices: false,
      isLeadOwner: false,
      isLeadTakeOver: false,
      isAllowCollaboratorRemoval: false,
      isShowInLeadProfile: false,
      isShowInSideRoute: false,
      showInFollowups: true,
      followUpDays: null,
      maxFollowUpDays: null,
      followUpLatestDealOnly: false,
      iconName: "",
      statuses: [],
      initialStatusCardsData: [
        {
          id: 1,
          statusName: "",
          subStatuses: [{ id: 0, name: "" }],
        },
      ],
    };
  }

  return {
    name: s.name ?? "",
    description: s.description ?? "",

    isTracks: !!s.isTracks,
    isChild: !!s.isChoices,
    maxChilds: s.maxChoices ?? 0,
    isSpecific: !!s.specificStatuses,
    isCollaborator: !!s.collaborator,
    isAnchorPoint: !!s.isAnchorPoint,
    anchorName: s.anchorName ?? null,
    verifyNewThisPeriod: s.verifyNewThisPeriod !== false,
    verifyExcludeRejected: s.verifyExcludeRejected !== false,
    verifyExcludeRollback: s.verifyExcludeRollback === true,
    verifyPreferActive: s.verifyPreferActive !== false,
    verifyTieBreakEarliest: s.verifyTieBreakEarliest !== false,
    verifyPreferLatestStage: s.verifyPreferLatestStage === true,
    verifyCreditOnce: s.verifyCreditOnce === true,
    verifyPeriodGrain: s.verifyPeriodGrain === "week" ? "week" : "month",
    isAllowCustomName: !!s.isAllowCustomName,
    isAutoPauseOtherChoices: !!s.isAutoPauseOther,
    isLeadOwner: !!s.isLeadOwnerAccess,
    isLeadTakeOver: !!s.isTakeOver,
    isAllowCollaboratorRemoval: !!s.isAllowCollabRemoval,
    isShowInLeadProfile: !!s.isShowInLead,
    isShowInSideRoute: !!s.isShowInRoute,
    showInFollowups: s.showInFollowups !== false,
    followUpDays: s.followUpDays ?? null,
    maxFollowUpDays: s.maxFollowUpDays ?? null,
    followUpLatestDealOnly: s.followUpLatestDealOnly === true,
    iconName: s.icon ?? "",

    statuses: transformStatusesForBackend(
      s.initialStatusCardsData,
      s.qualifyingStatus,
      s.defaultQualifyingSubStatus,
      s.rejectionStatuses,
      s.defaultRollbackStatusObj,
      s.rejectionSubStatuses
    ),
  };
};

export const {
  setEditinitialStatusCardsData,
  setEditStageIcon,
  setDefaultEditStageValues,
  setEditStagesName,
  setEditStagesDescription,
  setEditStagesIsTracks,
  setEditStagesIsChoices,
  setEditStagesSpecificStatuses,
  setEditStagesCollaborator,
  setEditStagesAnchorPoint,
  setEditStagesAnchorName,
  setEditStagesVerifyCondition,
  setEditStagesPeriodGrain,
  resetEditStages,
  setEditStagesIsShowInLead,
  setEditStagesIsShowInRightSide,
  setEditStagesShowInFollowups,
  setEditStagesFollowUpDays,
  setEditStagesMaxFollowUpDays,
  setEditStagesFollowUpLatestDealOnly,
  setEditStagesIsLeadOwner,
  setEditStagesIsTakeOver,
  setEditIsAllowCollabRemoval,
  setEditMaxChoices,
  setEditIsAllowCustomName,
  setEditIsAutoPauseOther,
  setEditQualifyingStatus,
  setEditDefaultQualifyingSubStatus,
  setEditRejectionStatuses,
  setEditRejectionSubStatuses,
  setCurrentRollBackStatusObj,
} = editStagesSlice.actions;

export default editStagesSlice.reducer;
